import './node-text.css';

import React, { MouseEvent, SyntheticEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { PositionedTree } from '../../../pages/mindmap/types';
import { useMindmap } from '../../../pages/mindmap/use-mindmap';
import { sanitizeHtml } from '../../../utils/sanitize-html';
import { checkIfSelectedLink } from '../../../utils/text-formatting/check-if-selected-link';
import { MIN_NODE_WIDTH } from '../node';
import { openInNewTab } from './../../../utils/open-in-new-tab';
import { checkIfClickedOnLink } from './../../../utils/text-formatting/check-if-clicked-on-link';
import { toUrl } from './../../../utils/to-url';

interface NodeTextProps {
  node: PositionedTree;
  selected: boolean;
  selectNode: (e: MouseEvent) => void;
  lineBreakWidth: number;
  setTempTextMinNewSize: (width: number, height: number) => void;
  unsetTempTextMinNewSize: () => void;
}

const TEXT_PADDING = 12;
const MIN_TEXT_HEIGHT = 21;

export const NodeText = ({
  node,
  selected,
  selectNode,
  lineBreakWidth,
  setTempTextMinNewSize,
  unsetTempTextMinNewSize,
}: NodeTextProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const [changedText, setChangedText] = useState<{ html: string; width: number; height: number } | undefined>();
  const {
    selection: { isEditingNode, setIsEditingNode, setSelectedNodeIds },
    dragAndDrop: { isAnyNodeDragging, isRootNodeDragging },
    manipulation: { updateText, shadowNodeAttachementsWidth },
    contextMenu: { setActiveMenu, hideContextMenu },
    interactableMap: { positionContextMenu },
  } = useMindmap();

  const contentEditable = selected && isEditingNode && (!isAnyNodeDragging || !isRootNodeDragging);

  useLayoutEffect(() => {
    // focus once node is editable
    if (contentEditable) ref.current?.focus();
  }, [contentEditable]);

  const onInput: React.FormEventHandler<HTMLDivElement> = (evt) => {
    const selection = window.getSelection();

    // check if the cursor is on the right side of a link, e.g. "google.com| is a search enigne"
    const caret = selection?.anchorNode;
    const hasCursorRightToLink = caret?.previousSibling?.nodeName == 'A' && selection?.anchorOffset == 1;
    if (hasCursorRightToLink) {
      // append to A element instead of adding new sibling next, e.g. when adding "B":
      // <a href="http://b/">b</a>B   --should turn into-->    <a href="http://b/">bB</a>
      const linkChildNodes = caret?.previousSibling.childNodes;
      linkChildNodes[linkChildNodes.length - 1].after(caret);

      // normalize inserted content
      caret.parentElement?.normalize();
    }

    // @ts-ignore
    const newWidth = evt.target.clientWidth;
    // @ts-ignore
    const newHeight = evt.target.clientHeight;
    // @ts-ignore
    const html = evt.target.innerHTML;

    onChangeHtml(newWidth, newHeight, html);
  };

  const updateHtml = () => {
    if (ref.current) onChangeHtml(ref.current.clientWidth, ref.current.clientHeight, ref.current.innerHTML);
  };

  const onChangeHtml = (newWidth: number, newHeight: number, html: string) => {
    setTempTextMinNewSize(newWidth, newHeight);
    setChangedText({
      html: html,
      height: newHeight,
      width: newWidth,
    });
  };

  useEffect(() => {
    // clear text selection when selected or unselected
    window?.getSelection()?.removeAllRanges();
  }, [selected]);

  const handleClick = (e: MouseEvent) => {
    const clickedOnLink = checkIfClickedOnLink(e);

    if (!selected) {
      if (clickedOnLink) {
        // @ts-ignore open link if node is not selected
        openInNewTab(clickedOnLink.href);
      } else {
        // select node on first click and set editing mode on second click
        selectNode(e);
      }
    }
    if (selected) {
      if (clickedOnLink && e.ctrlKey) {
        // @ts-ignore open link if node is selected and CTRL is pressed
        openInNewTab(clickedOnLink.href);
      } else {
        setIsEditingNode(true);
        // unselect all nodes except the currently editing one
        setSelectedNodeIds([node.id]);
      }
    }

    // dont recognize it as a map click
    e.preventDefault();
    e.stopPropagation();
  };

  const onBlur = () => {
    setIsEditingNode(false);
    if (changedText) {
      updateText(node.id, {
        ...changedText,
        width: shadowRef.current?.clientWidth || 0,
      });
    }
    unsetTempTextMinNewSize();
  };

  const [lastMouseDownLocation, setLastMouseDownLocation] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>): void => {
    setLastMouseDownLocation({ x: e.pageX, y: e.pageY });
  };

  const onPossibleClick = (e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>): void => {
    // check if possible click is an actual click or just a movement-click
    if (
      lastMouseDownLocation.y - 5 < e.pageY &&
      lastMouseDownLocation.y + 5 > e.pageY &&
      lastMouseDownLocation.x - 5 < e.pageX &&
      lastMouseDownLocation.x + 5 > e.pageX
    ) {
      // there was almost no movement between mouse down and click => handle it as a click on the node
      handleClick(e);
    }
  };

  const minHeight = 'calc(' + 2 * TEXT_PADDING + 'px + ' + MIN_TEXT_HEIGHT + 'px)';

  const onSelect = (e: SyntheticEvent<HTMLDivElement, Event>): void => {
    const selection = window.getSelection();
    const selectionString = selection?.toString();
    const hasSelectedText = selection && selectionString;

    if (hasSelectedText) {
      // if something was selected show the text formatting context menu
      setActiveMenu({ type: 'format-text', updateHtml });
      // @ts-ignore clientX and clientY usage
      positionContextMenu({ pageX: e.nativeEvent.clientX, pageY: e.nativeEvent.clientY + 8 }, false);
    } else {
      const selectedLink = checkIfSelectedLink();

      if (selectedLink) {
        // show text formatting context menu
        setActiveMenu({
          type: 'link-text',
          url: selectedLink.getAttribute('href') || '',
          onUpdateURL: (url) => {
            selectedLink.setAttribute('href', toUrl(url));
            updateHtml();
          },
        });
        // @ts-ignore clientX and clientY usage
        positionContextMenu({ pageX: e.nativeEvent.clientX, pageY: e.nativeEvent.clientY + 8 }, false);
      }
    }
  };

  // use initial text of node and let contenteditable control edits
  const initialNodeText = useMemo(() => node.text, [node.id]);

  return (
    <div>
      <div
        style={{
          width: lineBreakWidth, // max line width
          margin: -1, // handle parent border
          pointerEvents: 'none', // make this container not hoverable
          // use min height when text is empty
          height: ['\n', ''].includes(changedText?.html || node.text) ? minHeight : undefined,
        }}
      >
        <div
          data-is-root={node.pid == null}
          className="node-text-root"
          ref={ref}
          onMouseDown={onMouseDown}
          onClick={onPossibleClick}
          onBlur={onBlur}
          style={{
            minWidth: Math.max(MIN_NODE_WIDTH, node.attachementMinWidth, node.previewMinWidth),

            minHeight,
            // handle line breaking
            display: 'inline-block',
            overflowWrap: 'break-word', // break words when max-width is reached
            whiteSpace: 'break-spaces', // render and break all whitespaces
            // @ts-ignore tabIndex is used to focus the element
            tabIndex: 9999,
            // prevent selecting while not editing (e.g. while dragging nodes)
            userSelect: contentEditable ? 'text' : 'none',
            cursor: contentEditable ? 'text' : 'default',
            // hide caret cursor while content is not editing
            caretColor: contentEditable ? 'auto' : 'transparent',
            // rest styles
            padding: TEXT_PADDING,
            outline: 'none',
            pointerEvents: 'all',
          }}
          onInput={onInput}
          dangerouslySetInnerHTML={sanitizeHtml(initialNodeText)}
          // if it's selected and not dragging make the content editable in order that a next click
          // sets a cursor for typing in the right position
          // (but hide that it's editable via css until that next click)
          contentEditable={node.pid == null ? selected && !isRootNodeDragging : selected && !isAnyNodeDragging}
          suppressContentEditableWarning={true}
          onSelect={onSelect}
          // hide all context menus when capturing a mouse down event
          onMouseDownCapture={hideContextMenu}
        ></div>
      </div>
      {/* shadow element to measure text dimensions */}
      <div
        style={{
          width: lineBreakWidth, // max line width
          margin: -1, // handle parent border
          pointerEvents: 'none', // make this container not hoverable
          visibility: 'hidden',
          position: 'absolute',
        }}
      >
        <div
          id={`node-text-shadow-${node.id}`}
          ref={shadowRef}
          style={{
            minWidth: Math.max(
              MIN_NODE_WIDTH,
              node.previewMinWidth,
              shadowNodeAttachementsWidth === 0
                ? shadowNodeAttachementsWidth
                : shadowNodeAttachementsWidth || node.attachementMinWidth || 0,
            ),

            minHeight: 'calc(' + 2 * TEXT_PADDING + 'px + ' + MIN_TEXT_HEIGHT + 'px)',
            // handle line breaking
            display: 'inline-block',
            overflowWrap: 'break-word', // break words when max-width is reached
            whiteSpace: 'break-spaces', // render and break all whitespaces
            // rest styles
            padding: TEXT_PADDING,
          }}
          dangerouslySetInnerHTML={sanitizeHtml(changedText?.html || node.text)}
        ></div>
      </div>
    </div>
  );
};
